const CANVAS_TAP_MOVEMENT_THRESHOLD = 8;

/** Single-pointer click/tap recognition; drag, multi-touch, cancel and long hold never select. */
export class CanvasTapTracker {
  private readonly pointers = new Set<number>();
  private candidate: { id: number; x: number; y: number; at: number; cancelled: boolean } | null = null;
  begin(id: number, x: number, y: number, at: number): void {
    this.pointers.add(id);
    if (this.pointers.size > 1) { if (this.candidate) this.candidate.cancelled = true; return; }
    this.candidate = { id, x, y, at, cancelled: false };
  }
  move(id: number, x: number, y: number): void {
    if (this.candidate?.id === id && Math.hypot(x - this.candidate.x, y - this.candidate.y) > CANVAS_TAP_MOVEMENT_THRESHOLD) this.candidate.cancelled = true;
  }
  end(id: number, x: number, y: number, at: number, cancelled = false): boolean {
    this.move(id, x, y);
    this.pointers.delete(id);
    const candidate = this.candidate;
    if (!candidate || candidate.id !== id) return false;
    this.candidate = null;
    return !cancelled && !candidate.cancelled && this.pointers.size === 0 && at - candidate.at <= 700 && at >= candidate.at;
  }
  cancelPending(): void { if (this.candidate) this.candidate.cancelled = true; }
  reset(): void { this.pointers.clear(); this.candidate = null; }
}

export interface CameraLookInput {
  readonly dx: number;
  readonly dy: number;
  readonly source: "mouse" | "touch" | undefined;
}

/** Viewport-only pointers: a separate joystick finger never participates in this gesture. */
export class CanvasLookTracker {
  private readonly pointers = new Set<number>();
  private candidate: {
    id: number; originX: number; originY: number; x: number; y: number;
    source: "mouse" | "touch"; dragging: boolean; fresh: boolean;
  } | null = null;
  private dx = 0;
  private dy = 0;
  private source: "mouse" | "touch" | undefined;

  begin(id: number, x: number, y: number, source: "mouse" | "touch"): void {
    this.pointers.add(id);
    if (this.pointers.size > 1) {
      // Pinch owns this entire gesture, even after one finger is released.
      this.candidate = null;
      this.clearDelta();
      return;
    }
    this.candidate = { id, originX: x, originY: y, x, y, source, dragging: false, fresh: true };
  }

  move(id: number, x: number, y: number): void {
    const candidate = this.candidate;
    if (!candidate || candidate.id !== id) return;
    if (!candidate.fresh) {
      candidate.originX = candidate.x = x;
      candidate.originY = candidate.y = y;
      candidate.fresh = true;
      return;
    }
    if (!candidate.dragging && Math.hypot(x - candidate.originX, y - candidate.originY) <= CANVAS_TAP_MOVEMENT_THRESHOLD) return;
    candidate.dragging = true;
    const limit = candidate.source === "mouse" ? 240 : Number.POSITIVE_INFINITY;
    this.dx += Math.max(-limit, Math.min(limit, x - candidate.x));
    this.dy += Math.max(-limit, Math.min(limit, y - candidate.y));
    this.source = candidate.source;
    candidate.x = x;
    candidate.y = y;
  }

  end(id: number, cancelled = false): void {
    if (!this.pointers.delete(id)) return;
    if (this.candidate?.id === id) this.candidate = null;
    if (cancelled) this.clearDelta();
  }

  viewportChanged(): void {
    this.clearDelta();
    if (this.candidate) this.candidate.fresh = false;
  }

  consume(): CameraLookInput {
    const result = { dx: this.dx, dy: this.dy, source: this.source };
    this.clearDelta();
    return result;
  }

  reset(): void {
    this.pointers.clear();
    this.candidate = null;
    this.clearDelta();
  }

  private clearDelta(): void {
    this.dx = 0;
    this.dy = 0;
    this.source = undefined;
  }
}

/** Radians per second at full aim-stick deflection; mouse sensitivity lives in camera.ts. */
export const TPS_INPUT_CONFIG = Object.freeze({
  maximumMouseDelta: 240, aimYawRate: 2.35, aimPitchRate: 1.65,
  aimDeadzone: 0.14, aimResponse: 1.65,
  sprintForwardThreshold: 0.78, sprintResetThreshold: 0.25, sprintWindowMs: 400,
});

/** Requires a true return to neutral between two deliberate forward pushes. */
export class DoubleForwardSprint {
  active = false;
  private firstAt: number | undefined;
  private neutralSinceFirst = false;
  private aboveThreshold = false;

  sample(x: number, forward: number, at: number): void {
    const high = forward >= TPS_INPUT_CONFIG.sprintForwardThreshold;
    const neutral = Math.hypot(x, forward) <= TPS_INPUT_CONFIG.sprintResetThreshold;
    if (this.active) {
      if (forward < 0.45) this.reset();
      return;
    }
    if (this.firstAt !== undefined && (at < this.firstAt || at - this.firstAt > TPS_INPUT_CONFIG.sprintWindowMs)) {
      this.firstAt = undefined;
      this.neutralSinceFirst = false;
    }
    if (neutral && this.firstAt !== undefined) this.neutralSinceFirst = true;
    if (high && !this.aboveThreshold) {
      if (this.firstAt !== undefined && this.neutralSinceFirst) this.active = true;
      else if (this.firstAt === undefined) this.firstAt = at;
    }
    this.aboveThreshold = high;
  }

  reset(): void { this.active = false; this.firstAt = undefined; this.neutralSinceFirst = false; this.aboveThreshold = false; }
}

export interface HandActionEdges {
  readonly rightPressed: boolean; readonly rightReleased: boolean;
  readonly leftPressed: boolean; readonly leftReleased: boolean;
}
export interface MovementSample { readonly x: number; readonly y: number; readonly crouch: boolean; readonly sprint: boolean }
export type PointerLockState = "inactive" | "ready" | "requesting" | "locked" | "fallback";

type InputEvents = DocumentEventMap & WindowEventMap & HTMLElementEventMap;
class InputListeners {
  private readonly remove: (() => void)[] = [];
  add<K extends keyof InputEvents>(target: EventTarget, type: K, handler: (event: InputEvents[K]) => void, options?: AddEventListenerOptions): void {
    target.addEventListener(type, handler as EventListener, options);
    this.remove.push(() => target.removeEventListener(type, handler as EventListener, options));
  }
  destroy(): void { for (const remove of this.remove) remove(); this.remove.length = 0; }
}

/** Each instance owns one pointer; its ownership never includes viewport pinch pointers. */
class CapturedStick {
  private readonly events = new InputListeners();
  pointer: number | undefined;
  x = 0;
  y = 0;
  private originX = 0;
  private originY = 0;
  private maximum = 34;
  private knobX = 0;
  private knobY = 0;

  constructor(
    readonly element: HTMLElement, readonly knob: HTMLElement,
    private readonly beginAllowed: (id: number) => boolean,
    private readonly changed: (at: number, cancelled: boolean) => void,
    private readonly deadzone = 0.12,
  ) {
    this.events.add(element, "pointerdown", (event) => {
      if (this.pointer !== undefined || !this.beginAllowed(event.pointerId)) return;
      this.pointer = event.pointerId;
      this.measure();
      try { element.setPointerCapture(event.pointerId); } catch { /* Older WebViews and synthetic events. */ }
      this.move(event);
    });
    this.events.add(element, "pointermove", (event) => this.move(event));
    for (const type of ["pointerup", "pointercancel", "lostpointercapture"] as const) {
      this.events.add(element, type, (event) => {
        if (event.pointerId !== this.pointer) return;
        this.reset();
        this.changed(event.timeStamp, type !== "pointerup");
        event.preventDefault();
        event.stopPropagation();
      });
    }
  }

  viewportChanged(): void {
    this.measure();
    if (this.pointer !== undefined) this.knob.style.transform = `translate3d(${this.knobX * this.maximum}px,${this.knobY * this.maximum}px,0)`;
  }

  reset(): void {
    const pointer = this.pointer;
    this.pointer = undefined;
    this.x = this.y = this.knobX = this.knobY = 0;
    this.knob.style.transform = "translate3d(0,0,0)";
    if (pointer !== undefined) {
      try { this.element.releasePointerCapture(pointer); } catch { /* Already lost, or unsupported. */ }
    }
  }

  destroy(): void { this.reset(); this.events.destroy(); }

  private measure(): void {
    const bounds = this.element.getBoundingClientRect();
    this.originX = bounds.left + bounds.width / 2;
    this.originY = bounds.top + bounds.height / 2;
    this.maximum = Math.max(34, this.element.clientWidth * 0.32);
  }

  private move(event: PointerEvent): void {
    if (event.pointerId !== this.pointer) return;
    // A busy browser may merge a neutral return and the next forward push.
    // Reduce its real samples in order so the gesture keeps their timestamps.
    const samples = event.getCoalescedEvents?.();
    for (const sample of samples?.length ? samples : [event]) {
      if (sample.pointerId === this.pointer) this.sample(sample);
    }
    this.knob.style.transform = `translate3d(${this.knobX * this.maximum}px,${this.knobY * this.maximum}px,0)`;
    event.preventDefault();
    event.stopPropagation();
  }

  private sample(event: PointerEvent): void {
    const rawX = event.clientX - this.originX;
    const rawY = event.clientY - this.originY;
    const length = Math.hypot(rawX, rawY);
    const scale = length > this.maximum ? this.maximum / length : 1;
    this.knobX = rawX * scale / this.maximum;
    this.knobY = rawY * scale / this.maximum;
    const normalized = Math.hypot(this.knobX, this.knobY);
    const magnitude = normalized <= this.deadzone ? 0 : (normalized - this.deadzone) / (1 - this.deadzone);
    this.x = this.knobX / Math.max(0.001, normalized) * magnitude;
    this.y = -this.knobY / Math.max(0.001, normalized) * magnitude;
    this.changed(event.timeStamp, false);
  }
}

export class GameInput {
  private readonly events = new InputListeners();
  onCanvasTap: ((clientX: number, clientY: number) => void) | undefined;
  onCameraToggle: (() => void) | undefined;
  onControlStateChange: (() => void) | undefined;
  private readonly taps = new CanvasTapTracker();
  private readonly look = new CanvasLookTracker();
  private readonly moveStick: CapturedStick;
  private aimStick: CapturedStick | undefined;
  private crouchButton: HTMLElement | undefined;
  private readonly mobileSprint = new DoubleForwardSprint();
  axisX = 0;
  axisY = 0;
  private readonly pinchPointers = new Map<number, { x: number; y: number; fresh: boolean }>();
  private previousPinchDistance: number | undefined;
  private zoomMultiplier = 1;
  private zoomSource: "touch" | "wheel" | undefined;
  private readonly keys = new Set<string>();
  private touchVertical: -1 | 0 | 1 = 0;
  private readonly interactionSources = new Set<string>();
  private interactionPressed = false;
  private interactionReleased = false;
  private readonly uiBlockers = new Map<string, boolean>();
  private gameplayDisabled = false;
  private freeFly = false;
  private touchCrouch = false;
  private mode: "legacy" | "tps" = "legacy";
  private lockState: PointerLockState = "inactive";
  private mouseDx = 0;
  private mouseDy = 0;
  private rightHeld = false;
  private leftHeld = false;
  private rightPressed = false;
  private rightReleased = false;
  private leftPressed = false;
  private leftReleased = false;
  private pendingMovement: MovementSample | undefined;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    joystick: HTMLElement,
    knob: HTMLElement,
    interactionButton?: HTMLElement,
  ) {
    this.moveStick = new CapturedStick(joystick, knob,
      (id) => {
        if (this.keyboardInputBlocked() || this.canvasInputBlocked() || id === this.aimStick?.pointer) return false;
        this.taps.reset();
        return true;
      },
      (at, cancelled) => {
        if (cancelled) { this.mobileSprint.reset(); this.pendingMovement = undefined; }
        else this.mobileSprint.sample(this.moveStick.x, this.moveStick.y, at);
        this.update();
        if (!cancelled) this.retainMovement();
        this.onControlStateChange?.();
      });
    this.events.add(canvas, "pointerdown", (event) => {
      if (this.canvasInputBlocked()) return;
      if (event.pointerType !== "touch" && this.mode === "tps") {
        if (this.pointerLocked) {
          this.beginHand(event.button);
          event.preventDefault();
          return;
        }
        if (event.button !== 0) return;
        this.requestPointerLock();
        // While acquisition is pending, the activation click cannot use an item or select an NPC.
        if (this.lockState !== "fallback") return;
      }
      if (event.button !== 0) return;
      if (this.moveStick.pointer === undefined && this.aimStick?.pointer === undefined) this.taps.begin(event.pointerId, event.clientX, event.clientY, event.timeStamp);
      this.look.begin(event.pointerId, event.clientX, event.clientY, event.pointerType === "touch" ? "touch" : "mouse");
      try { canvas.setPointerCapture(event.pointerId); } catch { /* Synthetic events. */ }
    });
    this.events.add(canvas, "pointermove", (event) => {
      if (this.canvasInputBlocked() || this.pointerLocked) return;
      this.taps.move(event.pointerId, event.clientX, event.clientY);
      this.look.move(event.pointerId, event.clientX, event.clientY);
    });
    this.events.add(canvas, "pointerup", (event) => {
      this.endHand(event.button);
      this.look.end(event.pointerId);
      if (this.taps.end(event.pointerId, event.clientX, event.clientY, event.timeStamp, this.canvasInputBlocked() || this.pointerLocked)) this.onCanvasTap?.(event.clientX, event.clientY);
    });
    for (const type of ["pointercancel", "lostpointercapture"] as const) this.events.add(canvas, type, (event) => {
      this.taps.end(event.pointerId, event.clientX, event.clientY, event.timeStamp, true);
      this.look.end(event.pointerId, true);
      this.releaseHands();
    });
    this.events.add(canvas, "pointerdown", (event) => this.beginPinch(event));
    this.events.add(canvas, "pointermove", (event) => this.movePinch(event));
    for (const type of ["pointerup", "pointercancel", "lostpointercapture"] as const) this.events.add(canvas, type, (event) => this.endPinch(event));
    this.events.add(canvas, "wheel", (event) => this.zoomWithWheel(event), { passive: false });
    this.events.add(canvas, "contextmenu", (event) => { if (this.mode === "tps" && !this.canvasInputBlocked()) event.preventDefault(); });
    this.events.add(document, "mousemove", (event) => {
      if (!this.pointerLocked || this.canvasInputBlocked()) return;
      const limit = TPS_INPUT_CONFIG.maximumMouseDelta;
      if (Number.isFinite(event.movementX)) this.mouseDx += Math.max(-limit, Math.min(limit, event.movementX));
      if (Number.isFinite(event.movementY)) this.mouseDy += Math.max(-limit, Math.min(limit, event.movementY));
    });
    this.events.add(document, "pointerup", (event) => this.endHand(event.button));
    this.events.add(document, "pointerlockchange", () => {
      if (document.pointerLockElement === canvas && (this.mode !== "tps" || this.canvasInputBlocked())) {
        this.releasePointerLock();
        return;
      }
      if (document.pointerLockElement !== canvas) this.reset();
      else { this.taps.reset(); this.look.reset(); this.mouseDx = this.mouseDy = 0; }
      this.lockState = document.pointerLockElement === canvas ? "locked" : this.mode === "tps" ? "ready" : "inactive";
      this.onControlStateChange?.();
    });
    this.events.add(document, "pointerlockerror", () => this.pointerLockFailed());
    this.events.add(window, "keydown", (event) => {
      // Ctrl is a held stance modifier in owned TPS; mapped chords must not close/save/find the page.
      if (this.ownsPointerAim && event.ctrlKey && !event.altKey && !event.metaKey
        && (["KeyW", "KeyA", "KeyS", "KeyD", "KeyF"].includes(event.code) || /^Digit[1-5]$/.test(event.code))) event.preventDefault();
      if (isEditableTarget(event.target) || this.keyboardInputBlocked()) return;
      if (event.code === "KeyV" && !event.ctrlKey && !event.metaKey && !event.altKey) {
        if (!event.repeat) this.onCameraToggle?.();
        event.preventDefault();
        return;
      }
      this.keys.add(event.code);
      if (event.code === "ControlLeft" || event.code === "ControlRight" || event.code === "KeyC") this.mobileSprint.reset();
      if (event.code === "KeyE") {
        if (!event.repeat) this.beginInteraction("keyboard");
        event.preventDefault();
      }
      if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Space", "ControlLeft", "ControlRight"].includes(event.code)) event.preventDefault();
      this.update();
      if (!event.repeat) this.retainMovement();
    });
    this.events.add(window, "keyup", (event) => {
      this.keys.delete(event.code);
      if (event.code === "KeyE") this.endInteraction("keyboard");
      this.update();
    });
    if (interactionButton) {
      this.events.add(interactionButton, "pointerdown", (event) => {
        if (this.keyboardInputBlocked()) return;
        this.beginInteraction(`pointer:${event.pointerId}`);
        try { interactionButton.setPointerCapture(event.pointerId); } catch { /* Synthetic events. */ }
        event.preventDefault();
        event.stopPropagation();
      });
      for (const type of ["pointerup", "pointercancel", "lostpointercapture"] as const) this.events.add(interactionButton, type, (event) => {
        this.endInteraction(`pointer:${event.pointerId}`);
        event.preventDefault();
        event.stopPropagation();
      });
    }
    this.events.add(document, "focusin", (event) => {
      if (isEditableTarget(event.target)) { this.reset(); this.releasePointerLock(); }
    });
    const loseInput = (): void => { this.reset(); this.releasePointerLock(); };
    this.events.add(window, "blur", loseInput);
    this.events.add(window, "orientationchange", () => this.orientationChanged());
    this.events.add(document, "visibilitychange", () => { if (document.hidden) loseInput(); });
  }

  bindTouchControls(aim: HTMLElement, aimKnob: HTMLElement, crouch: HTMLElement): void {
    this.aimStick?.destroy();
    this.aimStick = new CapturedStick(aim, aimKnob,
      (id) => {
        if (this.mode !== "tps" || this.canvasInputBlocked() || id === this.moveStick.pointer) return false;
        this.taps.reset();
        return true;
      }, () => undefined, TPS_INPUT_CONFIG.aimDeadzone);
    this.crouchButton = crouch;
    const toggle = (): void => {
      if (this.keyboardInputBlocked() || this.freeFly) return;
      this.touchCrouch = !this.touchCrouch;
      this.mobileSprint.reset();
      crouch.setAttribute("aria-pressed", String(this.touchCrouch));
      this.retainMovement();
      this.onControlStateChange?.();
    };
    this.events.add(crouch, "pointerdown", (event) => {
      if (event.button !== 0 || this.keyboardInputBlocked()) return;
      // Touch click synthesis is unreliable for a third finger: act on its real pointer edge.
      toggle();
      try { crouch.setPointerCapture(event.pointerId); } catch { /* Synthetic events. */ }
      event.preventDefault();
      event.stopPropagation();
    });
    this.events.add(crouch, "click", (event) => {
      if (event.detail > 0) return;
      toggle(); // Keyboard/assistive activation has no pointerdown.
      event.preventDefault();
    });
  }

  update(): void {
    if (this.keyboardInputBlocked()) { this.axisX = this.axisY = 0; return; }
    const x = (this.keys.has("KeyD") || this.keys.has("ArrowRight") ? 1 : 0) - (this.keys.has("KeyA") || this.keys.has("ArrowLeft") ? 1 : 0);
    const y = (this.keys.has("KeyW") || this.keys.has("ArrowUp") ? 1 : 0) - (this.keys.has("KeyS") || this.keys.has("ArrowDown") ? 1 : 0);
    const length = Math.hypot(x, y);
    this.axisX = length > 0 ? x / length : this.moveStick.x;
    this.axisY = length > 0 ? y / length : this.moveStick.y;
  }

  /** One retained tap prevents a press/release between command ticks from disappearing. */
  peekMovementSample(): MovementSample {
    this.update();
    const current = { x: this.axisX, y: this.axisY, crouch: this.crouched, sprint: this.sprinting };
    const result = Math.hypot(current.x, current.y) > 0 || this.keyboardInputBlocked() ? current : this.pendingMovement ?? current;
    return { ...result, crouch: this.crouched, sprint: !this.crouched && result.sprint };
  }

  consumeMovementSample(): MovementSample {
    const result = this.peekMovementSample();
    this.pendingMovement = undefined;
    return result;
  }

  viewportChanged(): void {
    this.moveStick.viewportChanged();
    this.aimStick?.reset();
    this.aimStick?.viewportChanged();
    this.mobileSprint.reset();
    this.pendingMovement = undefined;
    this.taps.cancelPending();
    this.look.viewportChanged();
    this.mouseDx = this.mouseDy = 0;
    for (const point of this.pinchPointers.values()) point.fresh = false;
    this.previousPinchDistance = undefined;
  }

  private orientationChanged(): void {
    // A responsive mobile viewport emits orientationchange even when a physical keyboard remains held.
    // Legacy keeps its established held-stick resize behavior; TPS cancels only pointer-owned state.
    this.viewportChanged();
    if (this.mode === "legacy") return;
    this.moveStick.reset();
    this.aimStick?.reset();
    this.taps.reset();
    this.look.reset();
    this.mouseDx = this.mouseDy = 0;
    this.pinchPointers.clear();
    this.previousPinchDistance = undefined;
    this.zoomMultiplier = 1;
    this.zoomSource = undefined;
    this.touchVertical = 0;
    this.touchCrouch = false;
    this.crouchButton?.setAttribute("aria-pressed", "false");
    for (const source of this.interactionSources) if (source.startsWith("pointer:")) this.endInteraction(source);
    this.releaseHands();
    this.rightPressed = this.leftPressed = false;
    this.update();
    this.onControlStateChange?.();
  }

  get running(): boolean { return this.sprinting; }
  get sprinting(): boolean { return !this.keyboardInputBlocked() && !this.crouched && Math.hypot(this.axisX, this.axisY) > 0.1 && (this.keys.has("ShiftLeft") || this.keys.has("ShiftRight") || this.mobileSprint.active); }
  get crouched(): boolean { return !this.keyboardInputBlocked() && !this.freeFly && (this.touchCrouch || this.keys.has("KeyC") || this.keys.has("ControlLeft") || this.keys.has("ControlRight")); }
  get rightHandHeld(): boolean { return this.rightHeld; }
  get leftHandHeld(): boolean { return this.leftHeld; }
  get pointerLocked(): boolean { return document.pointerLockElement === this.canvas; }
  get pointerLockState(): PointerLockState { return this.lockState; }
  get cameraMode(): "legacy" | "tps" { return this.mode; }
  get ownsPointerAim(): boolean { return this.mode === "tps" && this.pointerLocked && !this.uiBlocked; }
  get gameplayActionsEnabled(): boolean { return !this.keyboardInputBlocked() && !this.uiBlocked; }
  get uiBlocked(): boolean { return this.uiBlockers.size > 0 || isEditableTarget(document.activeElement); }

  get verticalAxis(): -1 | 0 | 1 {
    if (this.keyboardInputBlocked()) return 0;
    if (this.keys.has("Space")) return 1;
    if (this.keys.has("ControlLeft") || this.keys.has("ControlRight") || this.keys.has("KeyC")) return -1;
    return this.touchVertical;
  }

  setDeveloperVertical(value: -1 | 0 | 1): void { this.touchVertical = value; }
  setDeveloperFreeFly(enabled: boolean): void { if (this.freeFly !== enabled) { this.freeFly = enabled; this.touchCrouch = false; this.mobileSprint.reset(); } }
  setGameplayDisabled(disabled: boolean): void { if (disabled !== this.gameplayDisabled) { this.gameplayDisabled = disabled; if (disabled) this.resetLocomotion(); } }
  setInventoryOpen(open: boolean): void { this.setUiBlocked("inventory", open); }
  /** Developer inspection panels release mouse ownership while retaining established keyboard fly controls. */
  setUiBlocked(source: string, blocked: boolean, blockKeyboard = true): void {
    if (blocked && this.uiBlockers.get(source) === blockKeyboard || !blocked && !this.uiBlockers.has(source)) return;
    if (blocked) { this.uiBlockers.set(source, blockKeyboard); this.reset(); this.releasePointerLock(); }
    else this.uiBlockers.delete(source);
    this.onControlStateChange?.();
  }
  setCameraMode(mode: "legacy" | "tps"): void {
    if (this.mode === mode) return;
    this.mode = mode;
    this.reset();
    this.releasePointerLock();
    this.lockState = mode === "tps" ? "ready" : "inactive";
    this.onControlStateChange?.();
  }

  /** Call only from a real canvas/resume activation. Change events decide actual lock ownership. */
  requestPointerLock(): void {
    if (this.mode !== "tps" || this.canvasInputBlocked() || this.pointerLocked || this.lockState === "requesting") return;
    if (typeof this.canvas.requestPointerLock !== "function") { this.pointerLockFailed(); return; }
    this.lockState = "requesting";
    this.onControlStateChange?.();
    try {
      const result = this.canvas.requestPointerLock();
      if (result && typeof result.catch === "function") void result.catch(() => this.pointerLockFailed());
    } catch { this.pointerLockFailed(); }
  }

  consumeZoom(): { readonly multiplier: number; readonly source: "touch" | "wheel" | undefined } {
    const result = { multiplier: this.zoomMultiplier, source: this.zoomSource };
    this.zoomMultiplier = 1;
    this.zoomSource = undefined;
    return result;
  }
  consumeCameraLook(): CameraLookInput {
    if (this.canvasInputBlocked()) { this.look.reset(); this.mouseDx = this.mouseDy = 0; }
    if (!this.pointerLocked) return this.look.consume();
    const result = { dx: this.mouseDx, dy: this.mouseDy, source: this.mouseDx || this.mouseDy ? "mouse" as const : undefined };
    this.mouseDx = this.mouseDy = 0;
    return result;
  }
  consumeAimStick(dt: number): { readonly yaw: number; readonly pitch: number } {
    if (!this.aimStick || this.mode !== "tps" || this.canvasInputBlocked()) return { yaw: 0, pitch: 0 };
    const length = Math.hypot(this.aimStick.x, this.aimStick.y);
    if (length === 0) return { yaw: 0, pitch: 0 };
    // The aim stick's dead zone has already been applied; add a gentle response curve.
    const amount = Math.pow(length, TPS_INPUT_CONFIG.aimResponse);
    const seconds = Number.isFinite(dt) ? Math.max(0, Math.min(0.1, dt)) : 0;
    const scale = amount * seconds / Math.max(0.001, length);
    return { yaw: -this.aimStick.x * scale * TPS_INPUT_CONFIG.aimYawRate, pitch: this.aimStick.y * scale * TPS_INPUT_CONFIG.aimPitchRate };
  }
  consumeInteractionPress(): boolean { const result = this.interactionPressed; this.interactionPressed = false; return result; }
  consumeInteractionRelease(): boolean { const result = this.interactionReleased; this.interactionReleased = false; return result; }
  consumeHandActions(): HandActionEdges {
    const edges = { rightPressed: this.rightPressed, rightReleased: this.rightReleased, leftPressed: this.leftPressed, leftReleased: this.leftReleased };
    this.rightPressed = this.rightReleased = this.leftPressed = this.leftReleased = false;
    return edges;
  }

  reset(): void {
    this.resetLocomotion();
    this.taps.reset();
    this.look.reset();
    this.aimStick?.reset();
    this.mouseDx = this.mouseDy = 0;
    this.pinchPointers.clear();
    this.previousPinchDistance = undefined;
    this.zoomMultiplier = 1;
    this.zoomSource = undefined;
  }

  /** Ragdoll/recovery blocks locomotion and item use while leaving the player's camera aim available. */
  private resetLocomotion(): void {
    this.moveStick.reset();
    this.mobileSprint.reset();
    this.touchCrouch = false;
    this.crouchButton?.setAttribute("aria-pressed", "false");
    this.axisX = this.axisY = 0;
    this.pendingMovement = undefined;
    this.touchVertical = 0;
    this.keys.clear();
    if (this.interactionSources.size > 0) this.interactionReleased = true;
    this.interactionSources.clear();
    this.interactionPressed = false;
    this.releaseHands();
    this.rightPressed = this.leftPressed = false;
  }

  destroy(): void {
    this.reset();
    this.releasePointerLock();
    this.moveStick.destroy();
    this.aimStick?.destroy();
    this.events.destroy();
  }

  private retainMovement(): void { if (Math.hypot(this.axisX, this.axisY) > 0) this.pendingMovement = { x: this.axisX, y: this.axisY, crouch: this.crouched, sprint: this.sprinting }; }
  private beginHand(button: number): void {
    if (this.gameplayDisabled || this.uiBlocked) return;
    if (button === 0 && !this.rightHeld) this.rightHeld = this.rightPressed = true;
    if (button === 2 && !this.leftHeld) this.leftHeld = this.leftPressed = true;
  }
  private endHand(button: number): void {
    if (button === 0 && this.rightHeld) { this.rightHeld = false; this.rightReleased = true; }
    if (button === 2 && this.leftHeld) { this.leftHeld = false; this.leftReleased = true; }
  }
  private releaseHands(): void { this.endHand(0); this.endHand(2); }
  private releasePointerLock(): void {
    if (this.pointerLocked) {
      try { document.exitPointerLock(); } catch { /* Embedded browsers can lose the owning document. */ }
    }
  }
  private pointerLockFailed(): void {
    if (this.mode !== "tps" || this.pointerLocked || this.canvasInputBlocked()) return;
    this.lockState = "fallback";
    this.onControlStateChange?.();
  }
  private beginPinch(event: PointerEvent): void {
    if (event.pointerType !== "touch" || this.pinchPointers.size >= 2 || this.canvasInputBlocked()) return;
    this.pinchPointers.set(event.pointerId, { x: event.clientX, y: event.clientY, fresh: true });
    try { this.canvas.setPointerCapture(event.pointerId); } catch { /* Synthetic events. */ }
    if (this.pinchReady()) this.previousPinchDistance = this.getPinchDistance();
    event.preventDefault();
  }
  private movePinch(event: PointerEvent): void {
    if (!this.pinchPointers.has(event.pointerId) || this.canvasInputBlocked()) return;
    this.pinchPointers.set(event.pointerId, { x: event.clientX, y: event.clientY, fresh: true });
    if (this.pinchReady()) {
      const distance = this.getPinchDistance();
      if (this.previousPinchDistance !== undefined && distance >= 8) { this.zoomMultiplier *= this.previousPinchDistance / distance; this.zoomSource = "touch"; }
      this.previousPinchDistance = distance;
    }
    event.preventDefault();
  }
  private endPinch(event: PointerEvent): void {
    if (!this.pinchPointers.delete(event.pointerId)) return;
    this.previousPinchDistance = this.pinchPointers.size === 2 ? this.getPinchDistance() : undefined;
  }
  private zoomWithWheel(event: WheelEvent): void {
    if (this.canvasInputBlocked()) return;
    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? Math.max(1, innerHeight) : 1;
    const delta = Math.max(-240, Math.min(240, event.deltaY * unit));
    if (!Number.isFinite(delta) || delta === 0) return;
    this.zoomMultiplier = Math.max(0.25, Math.min(4, this.zoomMultiplier * Math.exp(delta * 0.0015)));
    this.zoomSource = "wheel";
    event.preventDefault();
  }
  private getPinchDistance(): number {
    const [first, second] = [...this.pinchPointers.values()];
    return first && second ? Math.hypot(second.x - first.x, second.y - first.y) : 0;
  }
  private pinchReady(): boolean { return this.pinchPointers.size === 2 && [...this.pinchPointers.values()].every((point) => point.fresh); }
  private keyboardInputBlocked(): boolean { return this.gameplayDisabled || [...this.uiBlockers.values()].some(Boolean) || isEditableTarget(document.activeElement); }
  private canvasInputBlocked(): boolean { return this.uiBlocked; }
  private beginInteraction(source: string): void {
    if (this.keyboardInputBlocked()) return;
    if (this.interactionSources.size === 0) this.interactionPressed = true;
    this.interactionSources.add(source);
  }
  private endInteraction(source: string): void {
    if (this.interactionSources.delete(source) && this.interactionSources.size === 0) this.interactionReleased = true;
  }
}

export function isEditableTarget(target: EventTarget | null): boolean {
  const element = target instanceof HTMLElement ? target : null;
  return Boolean(element?.closest("input, textarea, select, [contenteditable]:not([contenteditable='false']), .inventory-shell"));
}
